import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Search, Menu, LogOut, LayoutDashboard, Settings, Heart, Shield, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
// Busca carrega só ao abrir (primeiro clique), fora do bundle inicial.
const loadSearch = () => import("@/components/search/SearchOverlay");
const SearchOverlay = lazy(() => loadSearch().then((m) => ({ default: m.SearchOverlay })));
import logoShoppingDoBem from "@/assets/logo-shopping-do-bem.webp";
import { CategoryMegaMenu, MobileCategories } from "./CategoryMegaMenu";
import { hoverBgCycle } from "@/lib/menuColors";


const topMenus = [
  {
    title: "Comece por aqui",
    links: [
      { label: "Como funciona", to: "/como-funciona" },
      { label: "Cadastre-se para apoiar", to: "/cadastro" },
      { label: "Cadastre uma causa", to: "/seja-parceiro" },
      { label: "Causas Beneficiadas", to: "/causas" },
      { label: "Sobre nós", to: "/sobre-nos" },
    ],
  },
  {
    title: "Aproveite",
    links: [
      { label: "Promoções", to: "/ofertas" },
      { label: "Cupons de desconto", to: "/cupons-de-desconto" },
      { label: "Lojas Parceiras", to: "/marcas" },
      { label: "Compare preços", to: "/comparador-de-precos" },
      { label: "Reviews de Produtos", to: "/blog-news" },
    ],
  },
] as const;

function TopMenus({ onInteractionChange }: { onInteractionChange: (active: boolean) => void }) {
  const [open, setOpen] = useState<number | null>(null);
  const root = useRef<HTMLElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();

  const cancelClose = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const delayClose = (index: number) => {
    cancelClose();
    timer.current = setTimeout(() => setOpen(current => current === index ? null : current), 160);
  };

  useEffect(() => { setOpen(null); }, [pathname]);
  useEffect(() => {
    onInteractionChange(open !== null);
    return () => onInteractionChange(false);
  }, [open, onInteractionChange]);
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(null);
        if (root.current?.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelClose();
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <nav ref={root} aria-label="Explorar Shopping do Bem" className="hidden shrink-0 items-center gap-1 lg:flex">
      {topMenus.map((menu, index) => (
        <div
          key={menu.title}
          className="relative"
          onPointerEnter={(event) => { if (event.pointerType === "mouse") { cancelClose(); setOpen(index); } }}
          onPointerLeave={(event) => { if (event.pointerType === "mouse") delayClose(index); }}
          onFocusCapture={() => { cancelClose(); setOpen(index); }}
          onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) delayClose(index); }}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={open === index}
            aria-controls={`top-menu-${index}`}
            onClick={(event) => { cancelClose(); setOpen(event.detail === 0 ? index : open === index ? null : index); }}
            className="gap-1 whitespace-nowrap text-foreground"
          >
            {menu.title}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === index ? "rotate-180" : ""}`} aria-hidden="true" />
          </Button>
          {open === index && (
            <div id={`top-menu-${index}`} className="absolute left-0 top-full z-60 min-w-56 border bg-popover p-1.5 text-popover-foreground shadow-lg">
              {menu.links.map((item, i) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(null)}
                  className={`block rounded px-3 py-2.5 text-sm font-medium ${hoverBgCycle[i % 4]} focus-visible:outline-2 focus-visible:outline-primary`}
                >{item.label}</Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}

export const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, profile, signOut, loading } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const { t } = useTranslation("public");

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate("/", { replace: true });
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const publicNavLinks = [
    { to: "/marcas", label: t("header.nav.brands") },
    { to: "/ofertas", label: "Ofertas" },
    { to: "/comparador-de-precos", label: "Comparar preços" },
    { to: "/blog-news", label: t("header.nav.reviews") },

    { to: "/causas", label: t("header.nav.causes") },
    { to: "/como-funciona", label: t("header.nav.howItWorks") },
    { to: "/sobre-nos", label: t("header.nav.about") },
  ];

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="container flex h-16 items-center justify-between gap-3">
          <Link to="/" className="flex items-center">
            <img
              src={logoShoppingDoBem}
              alt="Shopping do Bem"
              width={160}
              height={48}
              fetchPriority="high"
              decoding="async"
              className="h-12 w-auto"
            />
          </Link>

          {/* Search Trigger - Desktop */}
          <div className="hidden min-w-0 flex-1 max-w-xl md:flex">
            <Button variant="outline" type="button"
              onPointerEnter={loadSearch} onFocus={loadSearch} onTouchStart={loadSearch} onClick={() => setIsSearchOpen(true)}
              className="relative w-full justify-start gap-2 bg-muted/50 text-sm font-normal text-muted-foreground hover:bg-muted/50 hover:text-muted-foreground focus-visible:ring-0"
            >
              <Search className="h-4 w-4" />
              <span className="truncate">Buscar lojas, produtos e ofertas...</span>
            </Button>
          </div>

          <TopMenus onInteractionChange={setTopMenuOpen} />

          <div className="flex items-center gap-2">
            {/* Search Toggle - Mobile */}
            <Button variant="ghost" size="icon" className="md:hidden hover:bg-transparent focus-visible:ring-0" aria-label={t("header.search.placeholder")} onPointerEnter={loadSearch} onFocus={loadSearch} onTouchStart={loadSearch} onClick={() => setIsSearchOpen(true)}>
              <Search className="h-5 w-5" />
            </Button>

            {/* Auth Buttons/User Menu - Desktop only when logged in */}
            <div className="flex items-center gap-2">
              {loading ? <div className="h-9 w-24 rounded-md bg-muted animate-pulse" aria-hidden="true" /> : user ? (
                <DropdownMenu open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" aria-label={t("header.user.myAccount")} className="relative h-10 w-10 rounded-full hidden lg:flex">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "Avatar"} />
                        <AvatarFallback>{getInitials(profile?.full_name)}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <div className="flex flex-col space-y-1 p-2">
                      <p className="text-sm font-medium">{profile?.full_name || t("header.user.fallbackName")}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {t("header.user.myAccount")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/perfil" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        {t("header.user.settings")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/causas/trocar" className="cursor-pointer">
                        <Heart className="mr-2 h-4 w-4" />
                        {t("header.user.myCause")}
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <Shield className="mr-2 h-4 w-4" />
                          {t("header.user.adminPanel")}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t("header.user.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className="hidden sm:flex items-center gap-2">
                  <Button variant="ghost" asChild><Link to="/login">{t("header.auth.login")}</Link></Button>
                  <Button asChild><Link to="/cadastro">{t("header.auth.register")}</Link></Button>
                </span>
              )}
            </div>

            {/* Mobile Menu - always rendered on mobile */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menu" className="lg:hidden">
                  {user ? (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "Avatar"} />
                      <AvatarFallback>{getInitials(profile?.full_name)}</AvatarFallback>
                    </Avatar>
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] overflow-y-auto">
                {user ? (
                  <div className="flex flex-col gap-4 mt-8">
                    {/* Meu Perfil */}
                    <div>
                      <div className="flex items-center gap-3 pb-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "Avatar"} />
                          <AvatarFallback>{getInitials(profile?.full_name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0">
                          <p className="text-sm font-medium truncate">{profile?.full_name || t("header.user.fallbackName")}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("header.menu.myProfile")}</p>
                      <div className="flex flex-col">
                        <SheetClose asChild>
                          <Link to="/dashboard" className="flex items-center gap-3 py-2.5 text-sm font-medium hover:text-primary transition-colors">
                            <LayoutDashboard className="h-4 w-4" /> {t("header.user.myAccount")}
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link to="/perfil" className="flex items-center gap-3 py-2.5 text-sm font-medium hover:text-primary transition-colors">
                            <Settings className="h-4 w-4" /> {t("header.user.settings")}
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link to="/causas/trocar" className="flex items-center gap-3 py-2.5 text-sm font-medium hover:text-primary transition-colors">
                            <Heart className="h-4 w-4" /> {t("header.user.myCause")}
                          </Link>
                        </SheetClose>
                        {isAdmin && (
                          <SheetClose asChild>
                            <Link to="/admin" className="flex items-center gap-3 py-2.5 text-sm font-medium hover:text-primary transition-colors">
                              <Shield className="h-4 w-4" /> {t("header.user.adminPanel")}
                            </Link>
                          </SheetClose>
                        )}
                        <SheetClose asChild>
                          <button
                            onClick={handleSignOut}
                            className="flex items-center gap-3 py-2.5 text-sm font-medium text-destructive hover:opacity-80 transition-opacity text-left"
                          >
                            <LogOut className="h-4 w-4" /> {t("header.user.logout")}
                          </button>
                        </SheetClose>
                      </div>
                    </div>

                    <Separator />

                    {/* Navegar */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("header.menu.navigate")}</p>
                      <div className="flex flex-col">
                        {publicNavLinks.map((l) => (
                          <SheetClose key={l.to} asChild>
                            <Link to={l.to} className="py-2.5 text-sm font-medium hover:text-primary transition-colors">
                              {l.label}
                            </Link>
                          </SheetClose>
                        ))}
                        <SheetClose asChild><Link to="/cupons-de-desconto" className="py-2.5 text-sm font-medium hover:text-primary transition-colors">Cupons</Link></SheetClose>
                      </div>
                    </div>
                    <MobileCategories onNavigate={() => setMobileMenuOpen(false)} />
                  </div>
                ) : (
                  <nav className="flex flex-col gap-4 mt-8">
                    {publicNavLinks.map((l) => (
                      <SheetClose key={l.to} asChild>
                        <Link to={l.to} className="text-lg font-medium py-2 border-b">{l.label}</Link>
                      </SheetClose>
                    ))}
                    <SheetClose asChild><Link to="/cupons-de-desconto" className="text-lg font-medium py-2 border-b">Cupons</Link></SheetClose>
                    <MobileCategories onNavigate={() => setMobileMenuOpen(false)} />
                    <div className="flex flex-col gap-2 mt-4">
                      <SheetClose asChild>
                        <Button variant="outline" asChild className="w-full"><Link to="/login">{t("header.auth.login")}</Link></Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button asChild className="w-full"><Link to="/cadastro">{t("header.auth.register")}</Link></Button>
                      </SheetClose>
                    </div>
                  </nav>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <CategoryMegaMenu interactionActive={isSearchOpen || topMenuOpen || accountMenuOpen} />
      </header>

      {isSearchOpen && (
        <Suspense fallback={null}>
          <SearchOverlay open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
        </Suspense>
      )}
    </>
  );
};
